import { TEXT } from "./SbSkillsMapV2.constants";

// ─── API Response Transformers ────────────────────────────────────────────────
// These functions convert the /employee_jd_skill_analysis response into the
// shapes expected by the chart and proficiency table.

// Builds { skillName → maxCompositeImportance } from the tasks array.
// Composite = task_importance × skill_importance_within_task  (per API footnote).
export const buildSkillImportanceMap = (tasks = []) =>
  tasks.reduce((acc, task) => {
    const taskImportance = task.task_importance ?? 1;
    (task.skills ?? []).forEach(({ skill, importance }) => {
      const composite = taskImportance * (importance ?? 1);
      acc[skill] = Math.max(acc[skill] ?? 0, composite);
    });
    return acc;
  }, {});

// Sorts raw API skills by max(task_importance × skill_importance), descending.
// Skills not tied to any task rank last (0). Operates on raw skills (with .skill),
// so call it before transforming into table/chart shapes.
export const sortSkillsByImportance = (skills = [], tasks = []) => {
  const importanceBySkill = buildSkillImportanceMap(tasks);
  return [...skills].sort(
    (a, b) => (importanceBySkill[b.skill] ?? 0) - (importanceBySkill[a.skill] ?? 0)
  );
};

// Maps personProfileAnalysis.skills + tasks → array ready for SkillsChart.
// Skills are ranked by sortSkillImportance (max task×skill product); skills not
// tied to any task get 0 and sink to the bottom.
export const transformSkillsForChart = (skills = [], tasks = []) => {
  const importanceBySkill = buildSkillImportanceMap(tasks);
  return skills
    .map((s) => {
      const sortSkillImportance = importanceBySkill[s.skill] ?? 0;
      return {
        label:               s.skill,
        currentProficiency:  s.profile_proficiency  ?? 0,
        requiredProficiency: s.proficiency_needed ?? 0,
        importance:          sortSkillImportance,
        sortSkillImportance,
      };
    })
    .sort((a, b) => b.sortSkillImportance - a.sortSkillImportance);
};

// Maps personProfileAnalysis.skills → array ready for the proficiency table.
export const transformSkillsForProficiency = (skills = []) =>
  skills.map((s, idx) => ({
    skill_id:            s.skill_id || idx,
    skill_title:         s.skill,
    skill_description:   s.contextual_notes || "",
    current_proficiency: s.profile_proficiency  ?? 0,
    required_proficiency: s.proficiency_needed ?? 0,
    priority_skill:      s.priority ?? false,
  }));

// ─── Page-specific Text ───────────────────────────────────────────────────────

export const PAGE_TEXT = {
  CHART_TITLE: "Employee Skills Map",
  CHART_SUBTITLE:"(Bubble size shows skill proficiency required for the role)",
  SBI_LABEL: "SKILLSBRIDGE READINESS INDEX^ FOR SELECTED EMPLOYEE",
  PROFILE_LABEL: "EMPLOYEE PROFILE",
};

// ─── Skills Bridger Dropdown ──────────────────────────────────────────────────
// Stable — no runtime deps, defined once at module load.

export const SKILLS_BRIDGER_DROPDOWN_ITEMS = [
  { key: "skills-map", label: TEXT.DROPDOWN_SKILLS_MAP },
];
